import { Controller } from 'framework/core';
import { Singleton } from 'framework/di';
import { Signal, signal } from 'framework/store/signal';

import { DomHelper } from '../../helpers/dom.helper';
import { DTOHelper } from '../../helpers/dto.helper';
import { SlickHelper } from '../../helpers/slick.helper';
import { WCAGHelper } from '../../helpers/wcag.helper';
import type { ProductItemVariant } from '../../types';
import { AppService } from '../app/app.service';
import { CartAPI } from '../cart/cart.api';
import { CartController } from '../cart/cart.controller';
import { CombinedProductListingsController } from '../combined-product-listings/combined-product-listings.controller';
import { CombinedProductListingProduct } from '../combined-product-listings/combined-product-listings.model';
import { CombinedProductListingsService } from '../combined-product-listings/combined-product-listings.service';
import { PreOrderVariant } from '../pre-order/pre-order.model';
import { ProductAPI } from '../product/product.api';
import type { OptionsWithValues, ProductDataFromDOM, ProductImage } from '../product/product.model';
import { ProductSwatchService } from '../product/product-swatch.service';
import type { QuickViewAction } from './quick-view.model';

interface QuickViewProductData extends ProductDataFromDOM {
  handle?: string;
  splitProduct?: boolean;
  variantId?: string;
  selectedVariantImageByFilterOption?: string;
  variants: (ProductItemVariant & { combinedListingChildrens?: CombinedProductListingProduct[] })[];
}

interface QuickViewState {
  selectedVariant: PreOrderVariant | null;
  currentImageIndex: number;
}

@Singleton()
export class QuickViewController extends Controller {
  protected sliderRef: JQuery<HTMLElement> | null = null;
  protected buttonQuickviewFocus: HTMLElement | null = null;
  protected readonly QUICK_VIEW_CLOSE_AFTER_ADD_DELAY = 500;

  protected state: Signal<QuickViewState>;

  constructor(
    protected domHelper: DomHelper,
    protected dtoHelper: DTOHelper,
    protected wcagHelper: WCAGHelper,
    protected appService: AppService,
    protected cartAPI: CartAPI,
    protected productAPI: ProductAPI,
    protected swatchService: ProductSwatchService,
    protected cartController: CartController,
    protected slickHelper: SlickHelper,
    protected combinedProductListingController: CombinedProductListingsController,
    protected combinedProductListingsService: CombinedProductListingsService
  ) {
    super();
    this.state = signal({
      selectedVariant: null,
      currentImageIndex: 0,
    });
  }

  async open(action: QuickViewAction, triggerElement?: HTMLElement): Promise<void> {
    await this.slickHelper.load();

    const html = await this.fetchQuickViewHtml(action.productId);

    if (action.fromSelectOptionBtn) {
      this.injectHtml(this.hideImageSection(html));
    } else {
      this.injectHtml(html);
    }

    this.buttonQuickviewFocus = triggerElement || null;

    this.dispatchOpenEvent(action.productId);
    this.initSlider();
    this.initModal();
  }

  close(): void {
    const modal = document.getElementById('boost-sd__modal-quickview');
    if (modal) {
      modal.style.display = 'none';
      modal.remove();
      document.body.classList.remove('boost-sd__modal-open-quickview');
    }

    if (this.wcagHelper.isKeyboardActivated() && this.buttonQuickviewFocus) {
      this.buttonQuickviewFocus.focus();
    }

    this.state.value = { selectedVariant: null, currentImageIndex: 0 };
    this.combinedProductListingController.combinedListingState = null;
    this.combinedProductListingController.clearCombinedListingChildSession();
  }

  protected async fetchQuickViewHtml(productId: string | number): Promise<string> {
    const templateMetadata = this.appService.TAEAppConfig.templateMetadata;
    const params = {
      product_id: productId,
      quickview_widget_id: templateMetadata?.quickView || '',
    };

    const combinedTemplate =
      await this.combinedProductListingsService.fetchQuickViewTemplate(params);
    return combinedTemplate ?? (await this.productAPI.getProductDetail(params));
  }

  protected injectHtml(html: string): void {
    document.body.insertAdjacentHTML('beforeend', html);
    document.body.classList.add('boost-sd__modal-open-quickview');
  }

  protected hideImageSection(html: string): string {
    return html.replace(
      'class="boost-sd__quick-view-product-image"',
      'class="boost-sd__quick-view-product-image" data-disable-image="true" style="display:none"'
    );
  }

  protected dispatchOpenEvent(productId: string | number): void {
    window.dispatchEvent(
      new CustomEvent('boost-sd-open-quick-view', {
        detail: { pid: productId },
      })
    );
  }

  protected initSlider(): void {
    const isRtl = document.documentElement.getAttribute('dir') === 'rtl';
    const $ = this.slickHelper.jQuery;

    const slider = $('.boost-sd__quick-view .slick-slider').slick({
      prevArrow: false,
      nextArrow: false,
      useTransform: false,
      rtl: isRtl,
    } as unknown as JQuerySlickOptions);

    if (isRtl) {
      $('.boost-sd__quick-view .slick-slider').css({ direction: 'rtl' });
      $('.boost-sd__quick-view .slick-slider .slick-slide').css({ float: 'right' });
    }

    this.sliderRef = slider;

    const currentSlide = this.sliderRef.slick('slickCurrentSlide');
    this.selectThumbnail(typeof currentSlide === 'number' ? currentSlide : 0);

    this.sliderRef.on('beforeChange', (_event, _slick, _currentSlide, nextSlide) => {
      this.selectThumbnail(nextSlide as number);
    });

    this.initThumbnailEvents();
  }

  protected initThumbnailEvents(): void {
    const thumbnails = document.querySelectorAll('.boost-sd__thumbs-container-thumbs-item');
    thumbnails.forEach((thumbnail, index) => {
      thumbnail.addEventListener('click', () => {
        this.sliderRef?.slick('slickGoTo', index);
      });

      thumbnail.addEventListener('keydown', (e: Event) => {
        const keyboardEvent = e as KeyboardEvent;
        if (['Enter', ' ', 'Spacebar'].includes(keyboardEvent.key)) {
          this.sliderRef?.slick('slickGoTo', index);
        }
      });
    });
  }

  protected selectThumbnail(index: number): void {
    const SELECTED_CLASS = 'boost-sd__thumbs-container-thumbs-item--selected';
    const currentSelected = document.querySelector(`.${SELECTED_CLASS}`);
    currentSelected?.classList.remove(SELECTED_CLASS);

    const selectedThumbnail = document.querySelector(
      `.boost-sd__thumbs-container-thumbs-item:nth-child(${index + 1})`
    );
    selectedThumbnail?.classList.add(SELECTED_CLASS);
  }

  protected initModal(): void {
    const modal = document.getElementById('boost-sd__modal-quickview');
    if (!modal) return;

    const productData = this.parseProductData(modal);
    if (!productData) return;

    const { variants = [], images = [], options_with_values = [] } = productData;
    const preOrderVariants = variants as PreOrderVariant[];

    const mutableImages = [...images];
    if (productData.selectedVariantImageByFilterOption && mutableImages.length > 0) {
      mutableImages[0] = {
        ...mutableImages[0],
        src: productData.selectedVariantImageByFilterOption,
      };
    }

    const initialVariant = preOrderVariants.find((v) => v.available) || preOrderVariants[0] || null;
    this.state.value.selectedVariant = initialVariant;
    this.combinedProductListingController.initStateFromProductData(productData);

    this.initCloseHandlers(modal);
    this.initWCAGSupport(modal);
    this.initQuantityInput(modal);
    this.initSwatchSelection(modal, preOrderVariants, options_with_values);
    this.combinedProductListingController.initCombinedSwatchSelection(modal, (m) => {
      this.combinedProductListingController.saveCombinedListingChildToSession();
      const pd = this.parseProductData(m);
      if (pd) this.updateUI(m, pd);
    });
    this.initAddToCartButton(modal);
    this.initPreorderButton(modal);
    this.initBuyNowButton(modal);
    this.initThumbnailNavigation(modal, mutableImages);
    this.applySwatchSettings(modal, options_with_values);
    this.combinedProductListingController.initCombinedListingState(
      modal,
      preOrderVariants,
      () => this.state.value.selectedVariant,
      (modal, option, variants) => this.handleSwatchSelect(modal, option, variants)
    );
    this.combinedProductListingController.saveCombinedListingChildToSession();
    this.updateUI(modal, productData, mutableImages);
  }

  protected parseProductData(modal: HTMLElement): QuickViewProductData | null {
    const dataAttr = modal.getAttribute('data-product');
    if (!dataAttr) return null;
    return this.dtoHelper.safeParseJSON<QuickViewProductData>(dataAttr);
  }

  protected initCloseHandlers(modal: HTMLElement): void {
    const closeButton = modal.querySelector('.boost-sd__modal-close-btn');
    closeButton?.addEventListener('click', () => this.close());

    modal.addEventListener('click', (e) => {
      if (!(e.target as Element).closest('.boost-sd__modal-wrapper')) {
        this.close();
      }
    });
  }

  protected initWCAGSupport(modal: HTMLElement): void {
    this.wcagHelper.focusFirstElement(modal);

    const handleKeydown = (e: Event) => {
      this.wcagHelper.trapFocus(e as KeyboardEvent, modal);
    };

    modal.addEventListener('keydown', handleKeydown);
  }

  protected initQuantityInput(modal: HTMLElement): void {
    const inputNumber = modal.querySelector<HTMLInputElement>('.boost-sd__input-number-input');
    const decreaseButton = modal.querySelector('.boost-sd__input-number-btn-wrapper:first-child');
    const increaseButton = modal.querySelector('.boost-sd__input-number-btn-wrapper:last-child');
    const errorQuantity = modal.querySelector<HTMLElement>('.boost-sd__quick-view-quantity-error');

    if (!inputNumber) return;

    decreaseButton?.addEventListener('click', (e) => {
      e.preventDefault();
      inputNumber.value = String(Math.max(1, Number(inputNumber.value) - 1));
      if (errorQuantity) errorQuantity.style.display = 'none';
    });

    increaseButton?.addEventListener('click', (e) => {
      e.preventDefault();
      inputNumber.value = String(Number(inputNumber.value) + 1);
      if (errorQuantity) errorQuantity.style.display = 'none';
    });

    inputNumber.addEventListener('keypress', (e) => {
      const charCode = e.which || e.keyCode;
      if (charCode !== 46 && charCode > 31 && (charCode < 48 || charCode > 57)) {
        e.preventDefault();
      }
      if (errorQuantity) errorQuantity.style.display = 'none';
    });
  }

  protected initSwatchSelection(
    modal: HTMLElement,
    variants: PreOrderVariant[],
    optionsWithValues: OptionsWithValues[]
  ): void {
    const productSwatches = modal.querySelectorAll('.boost-sd__product-swatch');

    productSwatches.forEach((swatch) => {
      const swatchOptions = swatch.querySelectorAll('.boost-sd__product-swatch-option');

      swatchOptions.forEach((option) => {
        this.initSelectedOption(option, variants);

        option.addEventListener('click', () => {
          this.handleSwatchSelect(modal, option, variants);
        });

        option.addEventListener('keyup', (e: Event) => {
          const keyboardEvent = e as KeyboardEvent;
          if (['Enter', ' ', 'Spacebar'].includes(keyboardEvent.key)) {
            this.handleSwatchSelect(modal, option, variants);
          }
        });
      });
    });

    this.autoSelectFirstAvailableVariant(modal, variants);
    this.updateSoldOutLabels(modal, variants);
  }

  protected initSelectedOption(option: Element, variants: PreOrderVariant[]): void {
    const label = option.querySelector('.boost-sd__radio-label');
    if (!label) return;

    const selectedVariant = this.state.value.selectedVariant;
    if (!selectedVariant?.title) return;

    const optionTitle = this.swatchService.getSwatchOptionTitle(label);
    const variantOptions = selectedVariant.title.split(' / ');

    if (variantOptions.some((opt) => opt.toLowerCase() === optionTitle.toLowerCase())) {
      label.classList.add('boost-sd__radio-label--selected');
    }
  }

  protected handleSwatchSelect(
    modal: HTMLElement,
    option: Element,
    variants: PreOrderVariant[]
  ): void {
    const parentSwatch = option.closest('.boost-sd__product-swatch');
    if (!parentSwatch) return;

    // Combined listing: delegate to controller for parent swatch handling
    if (this.combinedProductListingController.combinedListingState) {
      this.combinedProductListingController.handleCombinedParentSwatchSelect(
        modal,
        option,
        variants,
        (selectedVariant, m) => {
          this.state.value.selectedVariant = selectedVariant;
          this.updateSoldOutOption(m, option, variants);
          const productData = this.parseProductData(m);
          if (productData) this.updateUI(m, productData);
        }
      );
      return;
    }

    this.swatchService.setSelectedLabel(parentSwatch, option);

    const variantTitle = this.swatchService.buildVariantTitleFromSelectedSwatches(modal);
    const selectedVariant = variants.find((v) => v.title === variantTitle) || null;

    this.state.value.selectedVariant = selectedVariant;

    this.updateSoldOutOption(modal, option, variants);

    const productData = this.parseProductData(modal);
    if (productData) {
      this.updateUI(modal, productData);
    }
  }

  protected autoSelectFirstAvailableVariant(modal: HTMLElement, variants: PreOrderVariant[]): void {
    const swatchOptions = modal.querySelectorAll('.boost-sd__product-swatch-option');
    const firstAvailableOption = Array.from(swatchOptions).find((option) => {
      const label = option.querySelector('.boost-sd__radio-label');
      return label && !label.classList.contains('boost-sd__radio-label--sold-out');
    });

    if (firstAvailableOption) {
      this.handleSwatchSelect(modal, firstAvailableOption, variants);
    }
  }

  protected updateSoldOutOption(
    modal: HTMLElement,
    _option: Element,
    variants: PreOrderVariant[]
  ): void {
    const allVariantsSoldOut = variants.every((variant) => !variant.available);

    if (allVariantsSoldOut) {
      modal.querySelectorAll('.boost-sd__radio-label').forEach((label) => {
        label.classList.add('boost-sd__radio-label--sold-out');
      });
      return;
    }

    this.updateSoldOutLabels(modal, variants);
  }

  protected updateSoldOutLabels(modal: HTMLElement, variants: PreOrderVariant[]): void {
    const swatchGroup = modal.querySelector('.boost-sd__product-swatch');
    if (!swatchGroup) return;

    swatchGroup.querySelectorAll('.boost-sd__product-swatch-option').forEach((option) => {
      const label = option.querySelector('.boost-sd__radio-label');
      if (!label) return;

      const optionValue = this.swatchService.getSwatchOptionTitle(label);
      // Also check original_merged_options because merged_options may contain metafield
      const variant = variants.find(
        (v) =>
          (v.title && v.title.trim() === optionValue) ||
          (v.merged_options && v.merged_options.some((mo: string) => mo.endsWith(optionValue))) ||
          (v.original_merged_options &&
            (v.original_merged_options as string[]).some((mo: string) => mo.endsWith(optionValue)))
      );

      if (!variant || !variant.available) {
        label.classList.add('boost-sd__radio-label--sold-out');
      } else {
        label.classList.remove('boost-sd__radio-label--sold-out');
      }
    });
  }

  protected initAddToCartButton(modal: HTMLElement): void {
    const atcButton = modal.querySelector<HTMLButtonElement>('.boost-sd__btn-add-to-cart');
    const inputNumber = modal.querySelector<HTMLInputElement>('.boost-sd__input-number-input');
    const errorQuantity = modal.querySelector<HTMLElement>('.boost-sd__quick-view-quantity-error');

    if (!atcButton) return;

    atcButton.addEventListener('click', async () => {
      const quantity = Number(inputNumber?.value || 1);
      if (quantity <= 0) {
        if (errorQuantity) errorQuantity.style.display = 'block';
        return;
      }

      const selectedVariant =
        this.combinedProductListingController.getCombinedListingVariantForCart(
          () => this.state.value.selectedVariant
        ) ?? this.state.value.selectedVariant;
      if (!selectedVariant?.id) return;

      const result = await this.cartController.addToCart(
        {
          productId: String(selectedVariant.id),
          quantity,
        },
        atcButton,
        true
      );

      if (result?.success) {
        setTimeout(() => {
          this.close();
          this.focusCartModal();
        }, this.QUICK_VIEW_CLOSE_AFTER_ADD_DELAY);
      }
    });
  }

  protected initBuyNowButton(modal: HTMLElement): void {
    const buyItNowButton = modal.querySelector<HTMLButtonElement>('.boost-sd__btn-buy-now');
    const inputNumber = modal.querySelector<HTMLInputElement>('.boost-sd__input-number-input');

    if (!buyItNowButton) return;

    buyItNowButton.addEventListener('click', async (e) => {
      e.stopImmediatePropagation();
      e.preventDefault();

      const quantity = Number(inputNumber?.value || 1);
      const variantId = (
        this.combinedProductListingController.getCombinedListingVariantForCart(
          () => this.state.value.selectedVariant
        ) ?? this.state.value.selectedVariant
      )?.id;
      if (quantity <= 0 || !variantId) return;

      try {
        const cart = await this.cartAPI.getAll();
        localStorage.setItem('backup_cart_items', JSON.stringify(cart.items));
        localStorage.setItem('buy_it_now_in_progress', 'true');

        await this.cartAPI.clearCart();
        await this.cartAPI.add(String(variantId), quantity);

        window.location.href = '/checkout';
      } catch (error) {
        this.appService.logger.error('Buy It Now failed:', error);
      }
    });
  }

  protected initPreorderButton(modal: HTMLElement): void {
    const preorderButton = modal.querySelector<HTMLButtonElement>('.boost-sd__button--preorder');
    const inputNumber = modal.querySelector<HTMLInputElement>('.boost-sd__input-number-input');

    if (!preorderButton) return;

    preorderButton.addEventListener('click', async () => {
      const selectedVariant =
        this.combinedProductListingController.getCombinedListingVariantForCart(
          () => this.state.value.selectedVariant
        ) ?? this.state.value.selectedVariant;
      if (!selectedVariant) return;

      const quantity = Number(inputNumber?.value || 1);
      if (quantity <= 0) return;

      const sellingPlanId = this.getSellingPlanId(selectedVariant);

      const result = await this.cartController.addToCart(
        {
          productId: String(selectedVariant.id),
          quantity,
          ...(sellingPlanId && { selling_plan: sellingPlanId }),
        },
        preorderButton,
        true
      );

      if (result?.success) {
        setTimeout(() => {
          this.close();
          this.focusCartModal();
        }, this.QUICK_VIEW_CLOSE_AFTER_ADD_DELAY);
      }
    });
  }

  protected focusCartModal(): void {
    setTimeout(() => {
      const cartModal = document.querySelector<HTMLElement>('#boost-sd__modal-cart');
      if (cartModal) {
        this.wcagHelper.focusFirstElement(cartModal);
      }
    }, 300);
  }

  protected initThumbnailNavigation(modal: HTMLElement, images: ProductImage[]): void {
    if (this.combinedProductListingController.combinedListingState) {
      this.combinedProductListingController.initCombinedListingThumbnailNavigation(modal, images, {
        getCurrentImageIndex: () => this.state.value.currentImageIndex,
        handleSliderNavigation: (index, imgs, thumbItems) =>
          this.handleSliderNavigation(index, imgs, thumbItems),
        updateNavigationButtons: (m, imageCount) => this.updateNavigationButtons(m, imageCount),
      });
      return;
    }

    const thumbItems = modal.querySelectorAll<HTMLElement>(
      '.boost-sd__thumbs-container-thumbs .boost-sd__thumbs-container-thumbs-item'
    );
    const thumbNextBtn = modal.querySelector<HTMLButtonElement>(
      '.boost-sd__thumbs-container-next-button button'
    );
    const thumbPrevBtn = modal.querySelector<HTMLButtonElement>(
      '.boost-sd__thumbs-container-prev-button button'
    );

    thumbPrevBtn?.addEventListener('click', () => {
      const newIndex = Math.max(0, this.state.value.currentImageIndex - 1);
      this.handleSliderNavigation(newIndex, images, thumbItems);
    });

    thumbNextBtn?.addEventListener('click', () => {
      const newIndex = Math.min(images.length - 1, this.state.value.currentImageIndex + 1);
      this.handleSliderNavigation(newIndex, images, thumbItems);
    });

    thumbItems.forEach((thumbnail, index) => {
      thumbnail.addEventListener('click', () => {
        this.handleSliderNavigation(index, images, thumbItems);
      });
    });

    this.handleSliderNavigation(0, images, thumbItems);
    this.updateNavigationButtons(modal, images.length);
  }

  protected handleSliderNavigation(
    index: number,
    images: ProductImage[],
    thumbItems: NodeListOf<HTMLElement>
  ): void {
    if (!images.length || !thumbItems.length) return;

    this.state.value.currentImageIndex = index;

    // Navigate to the correct slide directly using index
    this.sliderRef?.slick('slickGoTo', index);

    const portionSize = 4;
    const portion = Math.floor(index / portionSize);
    const startHiding = portion * portionSize;
    const endHiding = startHiding + portionSize - 1;

    thumbItems.forEach((thumbnail, i) => {
      if (i < startHiding || i > endHiding) {
        thumbnail.style.display = 'none';
      } else {
        thumbnail.style.display = 'block';
      }
    });

    const lastPortion = Math.floor((images.length - 1) / portionSize);
    if (portion === lastPortion && lastPortion > 0) {
      const lastPortionLength = images.length % portionSize || portionSize;
      const borrowFromPrevious = portionSize - lastPortionLength;

      for (let i = 0; i < borrowFromPrevious; i++) {
        const borrowIndex = images.length - lastPortionLength - 1 - i;
        if (thumbItems[borrowIndex]) {
          thumbItems[borrowIndex].style.display = 'block';
        }
      }
    }

    // Update navigation buttons state
    const modal = document.getElementById('boost-sd__modal-quickview');
    if (modal) {
      this.updateNavigationButtons(modal, images.length);
    }
  }

  protected updateNavigationButtons(modal: HTMLElement, imageCount: number): void {
    const thumbNextBtn = modal.querySelector<HTMLButtonElement>(
      '.boost-sd__thumbs-container-next-button button'
    );
    const thumbPrevBtn = modal.querySelector<HTMLButtonElement>(
      '.boost-sd__thumbs-container-prev-button button'
    );

    if (this.state.value.currentImageIndex === 0) {
      thumbPrevBtn?.setAttribute('disabled', 'true');
      thumbNextBtn?.removeAttribute('disabled');
    } else if (this.state.value.currentImageIndex === imageCount - 1) {
      thumbNextBtn?.setAttribute('disabled', 'true');
      thumbPrevBtn?.removeAttribute('disabled');
    } else {
      thumbPrevBtn?.removeAttribute('disabled');
      thumbNextBtn?.removeAttribute('disabled');
    }
  }

  protected applySwatchSettings(modal: HTMLElement, optionsWithValues: OptionsWithValues[]): void {
    const swatchSettings = this.appService.filterSettings?.swatch_settings;
    this.swatchService.applySwatchSettings(modal, optionsWithValues, swatchSettings);
  }

  protected updateUI(
    modal: HTMLElement,
    productData: QuickViewProductData,
    images?: ProductImage[]
  ): void {
    const selectedVariant = this.state.value.selectedVariant;

    // Combined listing: delegate all UI orchestration to the combined controller
    if (this.combinedProductListingController.combinedListingState) {
      this.combinedProductListingController.updateCombinedQuickViewUI(
        modal,
        selectedVariant,
        images ?? productData.images ?? [],
        this.sliderRef as { slick(method: string, ...args: unknown[]): unknown } | null,
        {
          updatePrice: (m, v) => this.updatePrice(m, v),
          updateAddToCartButton: (m, v) => this.updateAddToCartButton(m, v),
          updateLabels: (m, v) => this.updateLabels(m, v),
          updateDetailLink: (m, v) => this.updateDetailLink(m, productData, v),
          handleSliderNavigation: (index, imgs, thumbItems) =>
            this.handleSliderNavigation(index, imgs, thumbItems),
          updateNavigationButtons: (m, count) => this.updateNavigationButtons(m, count),
          selectThumbnail: (index) => this.selectThumbnail(index),
        }
      );
      return;
    }

    if (!selectedVariant) return;

    this.updatePrice(modal, selectedVariant);
    this.updateAddToCartButton(modal, selectedVariant);
    this.updateLabels(modal, selectedVariant);
    this.updateDetailLink(modal, productData, selectedVariant);
    this.updateSliderForVariant(modal, images || productData.images || [], selectedVariant);
  }

  protected updatePrice(modal: HTMLElement, selectedVariant: ProductItemVariant): void {
    const priceContainer = modal.querySelector('.boost-sd__quick-view-price');
    const priceSpan = modal.querySelector('.boost-sd__quick-view-price span');

    if (!priceContainer || !priceSpan) return;

    const showCurrencyCodes = this.appService.themeSettings?.quickView?.showCurrencyCodes;

    if (selectedVariant.price != null) {
      (priceContainer as HTMLElement).style.display = 'block';
      const formattedPrice = this.appService.formatCurrency(Number(selectedVariant.price), {
        showCurrencyCodes,
      });
      priceSpan.innerHTML = formattedPrice || '';
    } else {
      (priceContainer as HTMLElement).style.display = 'none';
    }

    const existingComparePrice = priceContainer.querySelector(
      '.boost-sd__format-currency--price-compare'
    );
    existingComparePrice?.remove();

    const compareAtPrice = Number(selectedVariant.compare_at_price) || 0;
    const price = Number(selectedVariant.price) || 0;
    const isSale = compareAtPrice > price;

    if (isSale) {
      const compareSpan = document.createElement('span');
      compareSpan.className = 'boost-sd__format-currency boost-sd__format-currency--price-compare';
      compareSpan.innerHTML =
        this.appService.formatCurrency(compareAtPrice, { showCurrencyCodes }) || '';
      priceSpan.insertAdjacentElement('afterend', compareSpan);
    }
  }

  protected updateAddToCartButton(
    modal: HTMLElement,
    selectedVariant: PreOrderVariant | null
  ): void {
    const btnBlock = this.domHelper.validateHTMLElement(
      modal.querySelector('.boost-sd__quick-view-btn')
    );

    const orderBlock = this.domHelper.validateHTMLElement(
      modal.querySelector('.boost-sd__quick-view-preorder')
    );

    const atcButton = btnBlock?.querySelector<HTMLButtonElement>('.boost-sd__btn-add-to-cart');
    const atcButtonText = atcButton?.querySelector('.boost-sd__button-text, span');
    const buyItNowButton = modal.querySelector<HTMLButtonElement>('.boost-sd__btn-buy-now');
    const preorderButton = modal.querySelector<HTMLButtonElement>('.boost-sd__button--preorder');
    const preorderButtonText = preorderButton?.querySelector('.boost-sd__button-text');
    const noteBlock = modal.querySelector<HTMLElement>('.boost-sd__preorder-note');

    if (!atcButton) return;

    const isSoldOut = !selectedVariant?.available;
    const isPreOrder = this.isPreOrderVariant(selectedVariant);
    const translation = this.appService.translation?.productItem as
      | Record<string, string>
      | undefined;

    // No matching variant — show unavailable state
    if (!selectedVariant) {
      this.combinedProductListingController.showCombinedUnavailableState(
        modal,
        translation?.unavailableLabel || 'Unavailable'
      );
      return;
    }

    this.updatePreOrderUI(modal, selectedVariant);

    if (isPreOrder) {
      if (btnBlock) btnBlock.style.display = 'none';
      if (orderBlock) orderBlock.style.display = 'block';
      if (preorderButtonText) {
        preorderButtonText.textContent = this.getPreOrderButtonText(selectedVariant);
      }
      if (noteBlock) {
        const noteText = this.getPreOrderNoteText(selectedVariant);
        if (noteText) {
          noteBlock.style.display = 'block';
          const noteTextEl = noteBlock.querySelector('.boost-sd__preorder-note-text');
          if (noteTextEl) noteTextEl.textContent = noteText;
        } else {
          noteBlock.style.display = 'none';
        }
      }
    } else if (isSoldOut) {
      if (btnBlock) btnBlock.style.display = 'block';
      if (orderBlock) orderBlock.style.display = 'none';
      atcButton.setAttribute('disabled', 'true');
      if (atcButtonText) {
        atcButtonText.textContent = translation?.soldoutLabel || 'Sold out';
      }
      if (buyItNowButton) {
        buyItNowButton.style.display = 'none';
      }
    } else {
      if (btnBlock) btnBlock.style.display = 'flex';
      if (orderBlock) orderBlock.style.display = 'none';
      atcButton.removeAttribute('disabled');
      if (atcButtonText) {
        atcButtonText.textContent = translation?.atcAvailableLabel || 'Add to cart';
      }
      if (buyItNowButton) {
        buyItNowButton.style.display = 'flex';
      }
    }

    atcButton.setAttribute('data-variant-id', String(selectedVariant.id || ''));
    buyItNowButton?.setAttribute('data-variant-id', String(selectedVariant.id || ''));
  }

  protected isPreOrderVariant(variant: PreOrderVariant | null): boolean {
    if (!variant) return false;
    return !!variant?.preorder && variant?.inventory_management !== null;
  }

  protected getPreOrderButtonText(variant: PreOrderVariant): string {
    return variant.preorder?.settings?.buttonText || 'Pre-Order';
  }

  protected getPreOrderNoteText(variant: PreOrderVariant): string | null {
    if (!variant.preorder?.settings?.showDeliveryText) return null;
    return variant.preorder?.settings?.deliveryText || null;
  }

  protected getSellingPlanId(variant: PreOrderVariant): string | null {
    return variant.preorder?.sellingPlan?.id || null;
  }

  protected updatePreOrderUI(modal: HTMLElement, selectedVariant: PreOrderVariant): void {
    const preorderBlocks = modal.querySelectorAll<HTMLElement>('.boost-sd__preorder');
    preorderBlocks.forEach((block) => {
      block.style.display = 'none';
    });

    const matchingBlock = modal.querySelector<HTMLElement>(
      `.boost-sd__preorder[data-variant-id="${selectedVariant.id}"]`
    );
    if (matchingBlock) {
      matchingBlock.style.display = 'block';
    }
  }

  protected updateLabels(modal: HTMLElement, selectedVariant: ProductItemVariant): void {
    const saleLabels = modal.querySelectorAll<HTMLElement>('.boost-sd__product-label--sale');
    const soldOutLabels = modal.querySelectorAll<HTMLElement>('.boost-sd__product-label--soldout');
    const customLabels = modal.querySelectorAll<HTMLElement>(
      '.boost-sd__product-label--customLabelByTag'
    );

    const isSoldOut = !selectedVariant.available;
    const compareAtPrice = Number(selectedVariant.compare_at_price) || 0;
    const price = Number(selectedVariant.price) || 0;
    const isSale = compareAtPrice > price;

    if (isSoldOut) {
      soldOutLabels.forEach((label) => (label.style.display = 'flex'));
      saleLabels.forEach((label) => (label.style.display = 'none'));
      customLabels.forEach((label) => (label.style.display = 'none'));
    } else if (isSale) {
      soldOutLabels.forEach((label) => (label.style.display = 'none'));
      saleLabels.forEach((label) => (label.style.display = 'flex'));
      customLabels.forEach((label) => (label.style.display = 'flex'));
    } else {
      soldOutLabels.forEach((label) => (label.style.display = 'none'));
      saleLabels.forEach((label) => (label.style.display = 'none'));
      customLabels.forEach((label) => (label.style.display = 'flex'));
    }
  }

  protected updateDetailLink(
    modal: HTMLElement,
    productData: QuickViewProductData,
    selectedVariant: ProductItemVariant
  ): void {
    const detailButton = modal.querySelector<HTMLAnchorElement>(
      '.boost-sd__quick-view-details-link'
    );
    if (!detailButton) return;

    const handle = productData.handle || '';
    const locale = (window?.Shopify?.routes?.root ?? '/').replace(/\/$/, '');
    const generalSettings = this.appService.TAEAppConfig.generalSettings;
    const addCollectionToProductUrl = generalSettings?.addCollectionToProductUrl;

    let detailUrl = this.buildProductDetailUrl(handle, locale, addCollectionToProductUrl);

    const { selectedChildVariant } =
      this.combinedProductListingController.combinedListingState || {};
    const selectedVariantId = selectedChildVariant?.id || selectedVariant.id;

    if (selectedVariantId && (productData.splitProduct || productData.variantId)) {
      detailUrl += `?variant=${selectedVariantId}`;
    } else if (selectedVariantId) {
      detailUrl += `?variant=${selectedVariantId}`;
    }

    detailButton.setAttribute('href', detailUrl);
  }

  protected buildProductDetailUrl(
    handle: string,
    locale: string,
    addCollectionToProductUrl?: boolean
  ): string {
    if (!handle) return '/';

    if (!addCollectionToProductUrl) {
      return `${locale}/products/${handle}`;
    }

    const pathname = window.location.pathname;
    const elements = pathname.split('/');

    if (pathname === '/' || pathname.includes('/search')) {
      return `${locale}/collections/all/products/${handle}`;
    }

    if (elements.includes('collections')) {
      const collectionHandleIndex = elements.indexOf('collections') + 1;
      const collectionHandle = elements[collectionHandleIndex] || 'all';
      return `${locale}/collections/${collectionHandle}/products/${handle}`;
    }

    return `${locale}/products/${handle}`;
  }

  protected updateSliderForVariant(
    modal: HTMLElement,
    images: ProductImage[],
    selectedVariant: ProductItemVariant
  ): void {
    if (!selectedVariant.image) return;

    const thumbItems = modal.querySelectorAll<HTMLElement>(
      '.boost-sd__thumbs-container-thumbs .boost-sd__thumbs-container-thumbs-item'
    );
    const imageIndex = images.findIndex((img) => img.src === selectedVariant.image);

    if (imageIndex >= 0) {
      this.handleSliderNavigation(imageIndex, images, thumbItems);
      this.updateNavigationButtons(modal, images.length);
    }
  }

  goToSlide(index: number): void {
    this.sliderRef?.slick('slickGoTo', index);
  }
}
